import { CalendarDays, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import { useCalendarNotes } from '../../hooks/useCalendarNotes';
import { useAppStore } from '../../store';
import { queryClient } from '../../api/queryClient';
import { MeetingCard } from './MeetingCard';

export function CalendarView() {
  const selectedDate = useAppStore((s) => s.selectedDate);
  const setSelectedMeetingId = useAppStore((s) => s.setSelectedMeetingId);

  const { data: events = [], isLoading, isFetching } = useCalendarEvents();
  const { data: notes = [] } = useCalendarNotes();

  const noteKeys = new Set(notes.map((n) => n.meetingKey));

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
  };

  return (
    <div className="px-6 py-4 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2.5">
          <CalendarDays size={18} className="text-accent" />
          <h2 className="text-headline text-text-primary">Calendar</h2>
          <span className="text-caption text-text-muted">
            {format(new Date(selectedDate + 'T00:00:00'), 'MMM d, yyyy')}
          </span>
        </div>
        <button
          onClick={handleRefresh}
          className="p-1.5 text-text-muted hover:text-text-secondary rounded-lg interactive-ghost transition-colors"
          title="Refresh calendar"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="surface-card p-4 flex gap-4 animate-pulse">
              <div className="w-12 space-y-1">
                <div className="h-3 bg-bg-raised rounded w-10" />
                <div className="h-2.5 bg-bg-raised rounded w-8" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-bg-raised rounded w-3/4" />
                <div className="h-3 bg-bg-raised rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && events.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <CalendarDays size={40} className="text-text-muted mb-4" strokeWidth={1.5} />
          <p className="text-title text-text-primary mb-1">No meetings today</p>
          <p className="text-body text-text-muted">Your calendar is clear — time to focus</p>
        </div>
      )}

      {/* Meeting list */}
      {!isLoading && events.length > 0 && (
        <div className="space-y-3">
          {events.map((event, index) => (
            <MeetingCard
              key={event.id}
              event={event}
              hasNote={noteKeys.has(event.id)}
              index={index}
              onClick={() => setSelectedMeetingId(event.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
